import { noop } from 'lodash';
import { useMemo } from 'react';

import { PrintTemplateSimple, PrintTemplateSimpleQuery } from '@dreryk/egabinet-document-template-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { Ordering } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   JustifyContent,
   Padding,
   TableColumnConfiguration,
   Text,
   ValueRendererProps,
} from '@dreryk/react-components';
import { Slot, TextPattern } from '@dreryk/watermelon';

import { patientConsentsAndAuthorizationsTranslationKeys } from '../../../../translations';

import { ConsentsAndAuthorizationsDocumentsMarkAsFavoriteIcon } from './cells/ConsentsAndAuthorizationsDocumentsMarkAsFavoriteIcon';
import { ConsentsAndAuthorizationsDocumentsOptionsCell } from './cells/ConsentsAndAuthorizationsDocumentsOptionsCell';
import { ConsentsAndAuthorizationsDocumentsUnmarkAsFavoriteIcon } from './cells/ConsentsAndAuthorizationsDocumentsUnmarkAsFavoriteIcon';

const PATIENT_CONSENT_CATEGORY = 'PATIENT_CONSENT';
const DEFAULT_ORDERINGS: Ordering<PrintTemplateSimple>[] = [
   {
      descending: true,
      property: 'favorite',
   },
   {
      descending: false,
      property: 'name',
   },
];

const t = patientConsentsAndAuthorizationsTranslationKeys.consentsAndAuthorizations.modal.form.table.columns.headers;

type UseConsentsAndAuthorizationsDocumentsDataConfigurationOptions = { searchText: string | null };

export const useConsentsAndAuthorizationsDocumentsDataConfiguration = (
   options: UseConsentsAndAuthorizationsDocumentsDataConfigurationOptions
): LazyLoadedDataConfigurationByCqrsQuery<PrintTemplateSimple> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<PrintTemplateSimple>(() => {
      const query = new PrintTemplateSimpleQuery();
      query.categories = Slot.of([PATIENT_CONSENT_CATEGORY]);
      if (options.searchText) {
         query.name = TextPattern.anywhereIgnoreCase(options.searchText);
      }
      return query;
   }, DEFAULT_ORDERINGS);
};

type UseConsentsAndAuthorizationsDocumentsTableColumnsOptions = {
   onApproveAndPrint: VoidFunction;
   onFavoriteChange: VoidFunction;
   patientId: number;
};

// eslint-disable-next-line max-lines-per-function
export const useConsentsAndAuthorizationsDocumentsTableColumns = (
   options: UseConsentsAndAuthorizationsDocumentsTableColumnsOptions
): TableColumnConfiguration<PrintTemplateSimple>[] => {
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         favorite: '32px',
         name: '100%',
         options: '104px',
      });

      return [
         {
            name: 'favorite',
            accessor: noop,
            width: columnsWidth.favorite,
            sortingDisabled: true,
            padding: Padding.NO_RIGHT,

            renderer: ({ bean }: ValueRendererProps<PrintTemplateSimple>) => {
               if (bean.favorite) {
                  return (
                     <ConsentsAndAuthorizationsDocumentsUnmarkAsFavoriteIcon
                        documentId={bean.id}
                        onUnmarkedAsFavorite={options.onFavoriteChange}
                     />
                  );
               }

               return (
                  <ConsentsAndAuthorizationsDocumentsMarkAsFavoriteIcon
                     documentId={bean.id}
                     onMarkedAsFavorite={options.onFavoriteChange}
                  />
               );
            },
         },
         {
            name: 'name',
            label: t.name,
            width: columnsWidth.name,
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<PrintTemplateSimple>) => (
               <Text testId={'name'} maxLines={3}>
                  {bean.name}
               </Text>
            ),
         },
         {
            width: columnsWidth.options,
            justifyContent: JustifyContent.START,
            accessor: noop,
            name: 'options',
            sortingDisabled: true,

            renderer: ({ bean }: ValueRendererProps<PrintTemplateSimple>) => (
               <ConsentsAndAuthorizationsDocumentsOptionsCell
                  printTemplateId={bean.id}
                  printTemplateName={bean.name}
                  patientId={options.patientId}
                  onApproveAndPrint={options.onApproveAndPrint}
               />
            ),
         },
      ];
   }, [options.onFavoriteChange, options.patientId, options.onApproveAndPrint]);
};
